import { NoteDefaultField } from '@/components/settings/NoteDefaultField';
import { hasSessionsWriteAccess, useOptionalAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useNoteDefaults } from '@/hooks/useNoteDefaults';
import {
  NOTE_DETAIL_LABELS,
  NOTE_DETAIL_LEVELS,
  NOTE_FORMAT_CHOICES,
  NOTE_FORMAT_LABELS,
  TEMPLATE_FORMAT,
} from '@/lib/noteStyle';
import type { NoteDefaults } from '@/types/noteStyle';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Icon,
  Stack,
  Text,
} from '@careflux/primitives';
import { Link } from 'wouter';

const DETAIL_OPTIONS = NOTE_DETAIL_LEVELS.map((value) => ({ value, label: NOTE_DETAIL_LABELS[value] }));
const FORMAT_OPTIONS = NOTE_FORMAT_CHOICES.map((value) => ({ value, label: NOTE_FORMAT_LABELS[value] }));

/** Default note detail/format (saved to settings features) and a link back into onboarding. */
export const NoteDefaultsSettings = () => {
  const { noteDefaults, saveNoteDefaults, isReady } = useNoteDefaults();
  const { toast } = useToast();
  const auth = useOptionalAuth();
  // Completing onboarding is rejected under impersonation and without sessions:write.
  const canEditProfile = !auth?.isImpersonating && hasSessionsWriteAccess(auth?.user ?? null);

  const save = (patch: Partial<NoteDefaults>) =>
    saveNoteDefaults(patch, {
      onSuccess: () => toast({ title: 'Stillingar vistaðar' }),
      onError: () => toast({ title: 'Villa', description: 'Mistókst að vista stillingar.', variant: 'destructive' }),
    });

  return (
    <Card padding="lg">
      <CardHeader>
        <CardTitle>
          <Box display="flex" align="center" gap="sm">
            <Icon name="FileText" color="primary" />
            <Text size="lg" weight="semibold">
              Nótur og prófíll
            </Text>
          </Box>
        </CardTitle>
        <CardDescription>
          Sjálfgefinn ítarleiki og snið nýrra nótna. Þú getur alltaf valið annað fyrir einstaka nótu.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Stack gap="lg" align="start">
          <NoteDefaultField
            id="note-detail-label"
            label="Ítarleiki nótu"
            options={DETAIL_OPTIONS}
            value={noteDefaults.noteDetail}
            disabled={!isReady}
            onChange={(noteDetail) => save({ noteDetail })}
          />
          <NoteDefaultField
            id="note-format-label"
            label="Snið"
            options={FORMAT_OPTIONS}
            value={noteDefaults.noteFormat ?? TEMPLATE_FORMAT}
            disabled={!isReady}
            onChange={(choice) => save({ noteFormat: choice === TEMPLATE_FORMAT ? null : choice })}
          />
          {canEditProfile && (
            <Button asChild variant="outline">
              <Link href="/onboarding">
                <Icon name="UserPen" size="sm" />
                Breyta prófíl
              </Link>
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
